import { TFunction } from 'react-i18next';
import { Tool } from 'app/types/tool';
import { formatDate } from 'utils/datetime';
import sort from 'utils/sort';
import { buildDeviceQrUrl } from 'utils/qr/parseQrValue';
import { loadQrLogo } from 'utils/qr/qrRenderer';

const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;
const MARGIN_MM = 15;
const LOGO_WIDTH_MM = 40;
const CELL_PADDING_MM = 1.5;
const FONT_SIZE = 9;
const TEXT_COLOR = '#000000';
const LINK_COLOR = '#1890ff';
const LINE_COLOR = '#d9d9d9';

type ReportColumn = {
  title: string;
  width: number;
  value: (tool: Tool) => string | undefined;
};

// Make and model link to the tool.
const LINKED_COLUMN_COUNT = 2;

// The public overview opens without a Kvalilog account; a tool without a QR code has none, so it gets no link.
const getToolUrl = (tool: Tool) => tool.QrCode ? buildDeviceQrUrl(tool.QrCode) : undefined;

const compareTools = (a: Tool, b: Tool) =>
  sort.func(a.Make, b.Make) || sort.func(a.Model, b.Model) || sort.func(a.SerialNumber, b.SerialNumber);

const formatOptionalDate = (date?: Date) => date ? formatDate(date) : '-';

/** A4 summary of the given tools: logo and generation date on top, then one row per tool. */
export async function generateToolReportPdf(tools: Tool[], unitName: string | undefined, t: TFunction<'translation', undefined>, siteAddress?: string) {
  const { jsPDF } = await import('jspdf');
  const logo = await loadQrLogo();

  const columns: ReportColumn[] = [
    { title: t('entities.tools.make'), width: 30, value: tool => tool.Make },
    { title: t('entities.tools.model'), width: 30, value: tool => tool.Model },
    { title: t('entities.tools.serialnumber'), width: 30, value: tool => tool.SerialNumber },
    { title: t('entities.tools.category'), width: 36, value: tool => tool.Category },
    { title: t('entities.tools.lastmaintenance'), width: 27, value: tool => formatOptionalDate(tool.LastMaintenanceDate) },
    { title: t('entities.tools.lasttest'), width: 27, value: tool => formatOptionalDate(tool.LastTestDate) },
  ];

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const logoHeight = LOGO_WIDTH_MM * logo.image.naturalHeight / logo.image.naturalWidth;
  pdf.addImage(logo.dataUrl, 'PNG', MARGIN_MM, MARGIN_MM, LOGO_WIDTH_MM, logoHeight, undefined, 'FAST');

  pdf.setFontSize(FONT_SIZE);
  pdf.text(formatDate(new Date()), PAGE_WIDTH_MM - MARGIN_MM, MARGIN_MM + logoHeight / 2, { align: 'right', baseline: 'middle' });

  let y = MARGIN_MM + logoHeight + 10;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.text(t('pages.tools.report.title'), MARGIN_MM, y);

  if (unitName || siteAddress) {
    y += 6;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    if (unitName) pdf.text(unitName, MARGIN_MM, y);
    if (siteAddress) pdf.text(siteAddress, PAGE_WIDTH_MM - MARGIN_MM, y, { align: 'right' });
  }

  y += 6;
  pdf.setFontSize(FONT_SIZE);
  const lineHeight = pdf.getLineHeight() / pdf.internal.scaleFactor;

  const splitCells = (texts: (string | undefined)[]): string[][] =>
    texts.map((text, index) => pdf.splitTextToSize(text || '-', columns[index].width - 2 * CELL_PADDING_MM));

  const getRowHeight = (cells: string[][]) => Math.max(...cells.map(lines => lines.length)) * lineHeight + 2 * CELL_PADDING_MM;

  const drawRow = (cells: string[][], url?: string) => {
    const height = getRowHeight(cells);

    let x = MARGIN_MM;
    cells.forEach((lines, index) => {
      pdf.setTextColor(url && index < LINKED_COLUMN_COUNT ? LINK_COLOR : TEXT_COLOR);
      pdf.text(lines, x + CELL_PADDING_MM, y + CELL_PADDING_MM, { baseline: 'top' });
      x += columns[index].width;
    });

    if (url) {
      const linkWidth = columns.slice(0, LINKED_COLUMN_COUNT).reduce((sum, column) => sum + column.width, 0);
      pdf.link(MARGIN_MM, y, linkWidth, height, { url });
    }

    y += height;
  };

  const drawHeader = () => {
    pdf.setFont('helvetica', 'bold');
    drawRow(splitCells(columns.map(column => column.title)));
    pdf.setDrawColor(TEXT_COLOR);
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN_MM, y, PAGE_WIDTH_MM - MARGIN_MM, y);
    pdf.setFont('helvetica', 'normal');
  };

  drawHeader();

  [ ...tools ].sort(compareTools).forEach(tool => {
    const cells = splitCells(columns.map(column => column.value(tool)));

    if (y + getRowHeight(cells) > PAGE_HEIGHT_MM - MARGIN_MM) {
      pdf.addPage();
      y = MARGIN_MM;
      drawHeader();
    }

    drawRow(cells, getToolUrl(tool));
    pdf.setDrawColor(LINE_COLOR);
    pdf.setLineWidth(0.1);
    pdf.line(MARGIN_MM, y, PAGE_WIDTH_MM - MARGIN_MM, y);
  });

  return pdf;
}
